import { useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { ModalOverlay } from './ui/ModalOverlay'
import { XCloseIcon, InfoIcon, PrimaryButton } from './ui'

interface DutyWhenUpdatingPolicyModalProps {
  onClose: () => void
  fieldName?: string
  policyId?: string
}

export default function DutyWhenUpdatingPolicyModal({ onClose, fieldName, policyId }: DutyWhenUpdatingPolicyModalProps) {
  const titleId = useId()
  const navigate = useNavigate()

  const handleContinue = () => {
    onClose()
    if (fieldName && policyId) {
      const base = `/policy/${encodeURIComponent(policyId)}`
      navigate(`${base}/update-all`)
    } else if (import.meta.env.DEV) {
      console.warn(
        '[DutyWhenUpdatingPolicyModal] Continue clicked but navigation skipped: missing policyId or fieldName',
        { policyId, fieldName }
      )
    }
  }

  return (
    <ModalOverlay
      open
      onClose={onClose}
      ariaLabelledBy={titleId}
      align="bottom-sheet"
      panelClassName="flex max-h-[calc(100vh-32px)] w-full max-w-[480px] flex-col overflow-hidden rounded-t-2xl bg-background-primary shadow-xl md:max-h-[calc(100vh-128px)] md:rounded-2xl"
    >
      <div className="flex min-h-0 flex-1 flex-col items-center overflow-hidden">
        <div className="flex min-h-0 w-full flex-1 flex-col items-start overflow-y-auto">
          <div className="relative flex w-full shrink-0 flex-col items-start gap-[16px] px-[16px] pb-[16px] pt-[20px]">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-[12px] top-[12px] z-10 flex size-[44px] cursor-pointer items-center justify-center overflow-clip rounded-md p-[8px]"
              aria-label="Close"
            >
              <div className="relative size-6 shrink-0 overflow-clip text-text-tertiary-600">
                <XCloseIcon className="h-6 w-6" />
              </div>
            </button>

            <div className="relative flex size-12 shrink-0 items-center justify-center overflow-clip rounded-full bg-background-warning-secondary">
              <div className="relative size-6 shrink-0 overflow-clip text-warning-primary">
                <InfoIcon className="h-6 w-6" />
              </div>
            </div>

            <div className="relative flex w-full shrink-0 flex-col items-start gap-[8px]">
              <h2
                id={titleId}
                className="relative w-full shrink-0 font-display text-display-xs font-semibold leading-[32px] text-text-primary-900"
              >
                Before you continue
              </h2>
              <div className="relative w-full shrink-0 font-body text-md font-regular leading-[24px] text-text-secondary-700">
                <p className="mb-[16px]">
                  It&apos;s important that you take reasonable care not to make a misrepresentation and to provide true,
                  accurate and complete answers.
                </p>
                <p className="mb-[16px]">
                  Inaccurate answers may affect your ability to claim on the policy or could result in your policy being
                  cancelled or treated as if it never existed.
                </p>
                <p className="mb-[16px]">
                  If you don&apos;t understand a question, please ask us for clarification. Further details about your
                  duty can be found in our PDS.
                </p>
                <p>
                  Any advice we provide is general in nature and does not take your personal circumstances into account.
                  You should consider your needs and read the PDS and TMD before making a decision to buy insurance or
                  make changes to your existing cover.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative flex w-full shrink-0 flex-col items-start border-t border-border-secondary bg-background-primary px-[16px] pb-[16px] pt-[24px] md:border-0 md:pb-[24px]">
          <PrimaryButton onClick={handleContinue} fullWidth>
            Continue
          </PrimaryButton>
        </div>
      </div>
    </ModalOverlay>
  )
}
